import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
// Import Types
import type { FilterTagRef } from 'types';
// Import UI Components
import { Button, Divider, Tooltip, Icon, SkeletonLoader } from '@geneui/components';
// Import Components
import FilterTagModals from './FilterTagModals';
// Import Hooks
import { useFilterTags, type UseFilterTagsParams } from './useFilterTags';
// Import Constants
import { l } from 'constants/common';
// Import Styles
import 'assets/scss/filterTag.scss';

export type { FilterTagRef } from 'types';

type Props = UseFilterTagsParams;

/**
 * Legacy stand-alone filter bar card (Add Filters, active chips, Save / Saved filters).
 * New list pages should use `FilterTagsProvider` together with `DataTable`'s `headerConfig` instead.
 */
const FilterTag = forwardRef<FilterTagRef, Props>((props, ref) => {
    const { filtersTagKey, isFilterHolderDisabled = false } = props;
    const { t } = useTranslation();
    const filterTags = useFilterTags(props);
    const {
        chips,
        changeFiltersList,
        handleAddFiltersClick,
        handleSaveFiltersClick,
        handleSavedFiltersClick,
        handleChipRemove,
    } = filterTags;

    useImperativeHandle(ref, () => ({ changeFiltersList }), [changeFiltersList]);

    return (
        <>
            <div className="crm-filter-tag-container">
                <div className="buttons-section">
                    <Button
                        disabled={isFilterHolderDisabled}
                        icon="bc-icon-add"
                        onClick={handleAddFiltersClick}
                        size="medium"
                    >
                        {isEmpty(chips) ? t(l.AddFilters) : ''}
                    </Button>

                    <Divider type="vertical" withSpace={true} />
                </div>
                <div className="tags-section">
                    <div>
                        {chips.map(({ label, value, valueFieldKey }, index) => {
                            const text = `${t(label)} = ${value}`;
                            const key = `${valueFieldKey}-${index}`;

                            return isEmpty(String(value ?? '').replaceAll(',', '')) ? (
                                <SkeletonLoader key={key} isBusy={true} />
                            ) : (
                                <Tooltip key={key} text={text} position="bottom">
                                    <div className="filter-tag">
                                        <p>{text}</p>
                                        <Icon onClick={() => handleChipRemove(index)} type="bc-icon-cancel" />
                                    </div>
                                </Tooltip>
                            );
                        })}
                    </div>
                </div>
                <div className="controls-section">
                    <Divider type="vertical" withSpace={true} />
                    <Button
                        icon="bc-icon-save"
                        disabled={isEmpty(chips) || isFilterHolderDisabled}
                        onClick={handleSaveFiltersClick}
                        size="medium"
                        appearance="minimal"
                    />
                    <Button
                        icon="bc-icon-settings-bars"
                        disabled={isFilterHolderDisabled}
                        onClick={handleSavedFiltersClick}
                        size="medium"
                        appearance="minimal"
                    />
                </div>
            </div>

            <FilterTagModals filtersTagKey={filtersTagKey} filterTags={filterTags} />
        </>
    );
});

FilterTag.displayName = 'FilterTag';

export default FilterTag;
